import { useEffect, useState } from "react";
import { Button, Card, Col, Container, Form, Row } from "react-bootstrap";
import RedirectContainer from "../../../components/RedirectContainer";
import SidebarPanelNavigation from "../../../components/SidebarPanelNavigation";
import LoadingIndicator from "../../../components/LoadingIndicator";
import useNotification from "../../../contexts/notification";
import { getThemeSettings, updateThemeSettings } from "../../../services/api";
import { applyThemeVariables, PRESETS, DEFAULT_COLORS } from "../../../utils/theme";

const COLOR_LABELS = {
  primary: "Primária",
  success: "Destaque (success)",
  secondary: "Secundária",
  danger: "Perigo (danger)",
  warning: "Aviso (warning)",
  info: "Info",
  dark: "Escuro (dark)",
  light: "Claro (light)",
};

export default function ThemeSettings() {
  const [colors, setColors] = useState(DEFAULT_COLORS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const { setMessage } = useNotification();

  useEffect(() => {
    getThemeSettings().then(
      (response) => {
        if (response?.data) {
          setColors((prev) => ({ ...prev, ...response.data }));
          applyThemeVariables(response.data);
        }
        setLoading(false);
      },
      () => {
        setLoading(false);
        setMessage({
          error: true,
          content: "Não foi possível carregar as cores do tema.",
        });
      }
    );
  }, [setMessage]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setColors((prev) => ({ ...prev, [name]: value }));
  };

  const applyPreset = (presetColors) => {
    setColors(presetColors);
    applyThemeVariables(presetColors);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSaving(true);

    try {
      const response = await updateThemeSettings(colors);
      if (response?.data) {
        setColors((prev) => ({ ...prev, ...response.data }));
        applyThemeVariables(response.data);
      }
      setMessage({
        error: false,
        content: "Tema atualizado com sucesso.",
      });
    } catch (error) {
      setMessage({
        error: true,
        content: "Não foi possível salvar o tema. Tente novamente.",
      });
    } finally {
      setSaving(false);
    }
  };

  const colorFields = Object.keys(COLOR_LABELS);

  return (
    <RedirectContainer role="admin">
      <Container fluid>
        <SidebarPanelNavigation isAdmin />
        <div className="content-panel">
          {loading && <LoadingIndicator />}
          {!loading && (
            <>
              <Row className="mt-3 mb-3">
                <Col>
                  <h5 className="mb-3">Presets de Eventos</h5>
                  <div className="d-flex flex-wrap gap-2">
                    {Object.entries(PRESETS).map(([key, preset]) => (
                      <Button
                        key={key}
                        variant="outline-primary"
                        size="sm"
                        onClick={() => applyPreset(preset.colors)}
                        style={{
                          borderColor: preset.colors.primary,
                          color: preset.colors.primary,
                        }}
                      >
                        {preset.label}
                      </Button>
                    ))}
                  </div>
                </Col>
              </Row>

              <Row>
                <Col lg={7}>
                  <Card>
                    <Card.Body>
                      <Card.Title className="text-primary">
                        Personalização de Cores
                      </Card.Title>
                      <Card.Text>
                        Atualize as cores do aplicativo. Use os presets acima
                        para eventos sazonais (Black Friday, Natal, etc.).
                      </Card.Text>
                      <Form onSubmit={handleSubmit}>
                        <Row>
                          {colorFields.map((field) => (
                            <Col md={6} key={field}>
                              <Form.Group controlId={`theme-${field}`}>
                                <Form.Label>
                                  Cor {COLOR_LABELS[field]}
                                </Form.Label>
                                <Form.Control
                                  type="color"
                                  name={field}
                                  value={colors[field] || DEFAULT_COLORS[field]}
                                  onChange={handleChange}
                                />
                              </Form.Group>
                            </Col>
                          ))}
                        </Row>
                        <Button
                          className="mt-3"
                          type="submit"
                          disabled={saving}
                        >
                          {saving ? "Salvando..." : "Salvar alterações"}
                        </Button>
                      </Form>
                    </Card.Body>
                  </Card>
                </Col>
                <Col lg={5} className="mt-3 mt-lg-0">
                  <Card>
                    <Card.Body>
                      <Card.Title className="text-primary">
                        Pré-visualização
                      </Card.Title>
                      <Card.Text>
                        Exemplo de botões e destaques com as cores atuais.
                      </Card.Text>

                      <div className="d-flex flex-column gap-2 mb-3">
                        <div className="d-flex flex-wrap gap-2 mb-2">
                          <Button className="border-0 text-white" style={{ backgroundColor: colors.primary }}>
                            Primário
                          </Button>
                          <Button className="border-0 text-white" style={{ backgroundColor: colors.success }}>
                            Destaque
                          </Button>
                          <Button className="border-0 text-white" style={{ backgroundColor: colors.secondary }}>
                            Secundário
                          </Button>
                          <Button className="border-0 text-white" style={{ backgroundColor: colors.danger }}>
                            Perigo
                          </Button>
                          <Button className="border-0" style={{ backgroundColor: colors.warning, color: colors.dark }}>
                            Aviso
                          </Button>
                          <Button className="border-0 text-white" style={{ backgroundColor: colors.info }}>
                            Info
                          </Button>
                        </div>
                      </div>

                      <div className="d-flex flex-column gap-1">
                        <span style={{ color: colors.primary, fontWeight: 700 }}>
                          Texto cor primária
                        </span>
                        <span style={{ color: colors.success, fontWeight: 700 }}>
                          Texto cor destaque
                        </span>
                        <span style={{ color: colors.danger, fontWeight: 700 }}>
                          Texto cor perigo
                        </span>
                        <span style={{ color: colors.info, fontWeight: 700 }}>
                          Texto cor info
                        </span>
                      </div>

                      <hr />
                      <div className="d-flex flex-wrap gap-1">
                        <span className="badge" style={{ backgroundColor: colors.primary, color: "#fff" }}>
                          badge primary
                        </span>
                        <span className="badge" style={{ backgroundColor: colors.success, color: "#fff" }}>
                          badge success
                        </span>
                        <span className="badge" style={{ backgroundColor: colors.danger, color: "#fff" }}>
                          badge danger
                        </span>
                        <span className="badge" style={{ backgroundColor: colors.warning, color: colors.dark }}>
                          badge warning
                        </span>
                      </div>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </>
          )}
        </div>
      </Container>
    </RedirectContainer>
  );
}
